<template>
  <component
    :is="tag"
    class="app-col"
    :class="colClasses"
    v-bind="$attrs"
  >
    <slot />
  </component>
</template>

<script>
/**
 * AppCol — columna con sistema de 12 columnas y breakpoints.
 *
 * Reemplazo gradual de b-col / .col-*.
 * Reglas (spec 005):
 *  - HTML plano, CSS propio, sin tags b-* ni clases Bootstrap.
 *  - Breakpoints sm 576px / md 768px / lg 992px / xl 1200px.
 *  - Sistema de 12 columnas.
 *  - inheritAttrs: false + v-bind="$attrs" al root.
 *
 * Props:
 *  - tag: elemento root. Default 'div'.
 *  - cols: ancho por defecto (1-12). null = auto (flex 1 1 0). true = auto.
 *  - sm/md/lg/xl: ancho por breakpoint (1-12). null = inherit. true = auto.
 *  - offset/offsetSm/offsetMd/offsetLg/offsetXl: desplazamiento (1-12).
 *  - order/orderSm/orderMd/orderLg/orderXl: orden visual (number).
 *  - alignSelf: align-self de flex. Default null.
 */
const BREAKPOINTS = {
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200
}

function colWidth(n) {
  return `calc((100% / 12) * ${n})`
}

export default {
  name: 'AppCol',
  inheritAttrs: false,
  props: {
    tag: { type: String, default: 'div' },
    cols: { type: [String, Number, Boolean], default: null },
    sm: { type: [String, Number, Boolean], default: null },
    md: { type: [String, Number, Boolean], default: null },
    lg: { type: [String, Number, Boolean], default: null },
    xl: { type: [String, Number, Boolean], default: null },
    offset: { type: [String, Number], default: null },
    offsetSm: { type: [String, Number], default: null },
    offsetMd: { type: [String, Number], default: null },
    offsetLg: { type: [String, Number], default: null },
    offsetXl: { type: [String, Number], default: null },
    order: { type: [String, Number], default: null },
    orderSm: { type: [String, Number], default: null },
    orderMd: { type: [String, Number], default: null },
    orderLg: { type: [String, Number], default: null },
    orderXl: { type: [String, Number], default: null },
    alignSelf: { type: String, default: null }
  },
  computed: {
    // Genera clases tipo: app-col--cols-6, app-col--sm-6, app-col--md-4, etc.
    colClasses() {
      const classes = []

      // Ancho base (sin breakpoint)
      if (this.cols !== null) {
        if (this.cols === true) {
          classes.push('app-col--cols-auto')
        } else {
          classes.push(`app-col--cols-${this.cols}`)
        }
      }

      // Ancho por breakpoint
      const bpProps = [
        { prop: 'sm', key: 'sm' },
        { prop: 'md', key: 'md' },
        { prop: 'lg', key: 'lg' },
        { prop: 'xl', key: 'xl' }
      ]

      for (const { prop, key } of bpProps) {
        if (this[prop] !== null) {
          if (this[prop] === true) {
            classes.push(`app-col--${key}-auto`)
          } else {
            classes.push(`app-col--${key}-${this[prop]}`)
          }
        }
      }

      // Offsets
      if (this.offset !== null) classes.push(`app-col--offset-${this.offset}`)
      if (this.offsetSm !== null) classes.push(`app-col--offset-sm-${this.offsetSm}`)
      if (this.offsetMd !== null) classes.push(`app-col--offset-md-${this.offsetMd}`)
      if (this.offsetLg !== null) classes.push(`app-col--offset-lg-${this.offsetLg}`)
      if (this.offsetXl !== null) classes.push(`app-col--offset-xl-${this.offsetXl}`)

      // Orders
      if (this.order !== null) classes.push(`app-col--order-${this.order}`)
      if (this.orderSm !== null) classes.push(`app-col--order-sm-${this.orderSm}`)
      if (this.orderMd !== null) classes.push(`app-col--order-md-${this.orderMd}`)
      if (this.orderLg !== null) classes.push(`app-col--order-lg-${this.orderLg}`)
      if (this.orderXl !== null) classes.push(`app-col--order-xl-${this.orderXl}`)

      // Align self
      if (this.alignSelf !== null) {
        classes.push(`app-col--align-self-${this.alignSelf}`)
      }

      return classes
    }
  }
}
</script>

<style scoped>
.app-col {
  --app-row-gutter-x: 16px;
  position: relative;
  width: 100%;
  padding-left: calc(var(--app-row-gutter-x, 16px) / 2);
  padding-right: calc(var(--app-row-gutter-x, 16px) / 2);
  box-sizing: border-box;
}

/* Base sin breakpoint */
.app-col--cols-auto {
  flex: 1 1 0;
  max-width: 100%;
}

.app-col--cols-1  { flex: 0 0 calc((100% / 12) * 1);  max-width: calc((100% / 12) * 1); }
.app-col--cols-2  { flex: 0 0 calc((100% / 12) * 2);  max-width: calc((100% / 12) * 2); }
.app-col--cols-3  { flex: 0 0 calc((100% / 12) * 3);  max-width: calc((100% / 12) * 3); }
.app-col--cols-4  { flex: 0 0 calc((100% / 12) * 4);  max-width: calc((100% / 12) * 4); }
.app-col--cols-5  { flex: 0 0 calc((100% / 12) * 5);  max-width: calc((100% / 12) * 5); }
.app-col--cols-6  { flex: 0 0 calc((100% / 12) * 6);  max-width: calc((100% / 12) * 6); }
.app-col--cols-7  { flex: 0 0 calc((100% / 12) * 7);  max-width: calc((100% / 12) * 7); }
.app-col--cols-8  { flex: 0 0 calc((100% / 12) * 8);  max-width: calc((100% / 12) * 8); }
.app-col--cols-9  { flex: 0 0 calc((100% / 12) * 9);  max-width: calc((100% / 12) * 9); }
.app-col--cols-10 { flex: 0 0 calc((100% / 12) * 10); max-width: calc((100% / 12) * 10); }
.app-col--cols-11 { flex: 0 0 calc((100% / 12) * 11); max-width: calc((100% / 12) * 11); }
.app-col--cols-12 { flex: 0 0 calc((100% / 12) * 12); max-width: calc((100% / 12) * 12); }

/* Offset sin breakpoint */
.app-col--offset-1  { margin-left: calc((100% / 12) * 1); }
.app-col--offset-2  { margin-left: calc((100% / 12) * 2); }
.app-col--offset-3  { margin-left: calc((100% / 12) * 3); }
.app-col--offset-4  { margin-left: calc((100% / 12) * 4); }
.app-col--offset-5  { margin-left: calc((100% / 12) * 5); }
.app-col--offset-6  { margin-left: calc((100% / 12) * 6); }
.app-col--offset-7  { margin-left: calc((100% / 12) * 7); }
.app-col--offset-8  { margin-left: calc((100% / 12) * 8); }
.app-col--offset-9  { margin-left: calc((100% / 12) * 9); }
.app-col--offset-10 { margin-left: calc((100% / 12) * 10); }
.app-col--offset-11 { margin-left: calc((100% / 12) * 11); }
.app-col--offset-12 { margin-left: calc((100% / 12) * 12); }

/* Order sin breakpoint */
.app-col--order-0  { order: 0; }
.app-col--order-1  { order: 1; }
.app-col--order-2  { order: 2; }
.app-col--order-3  { order: 3; }
.app-col--order-4  { order: 4; }
.app-col--order-5  { order: 5; }
.app-col--order-6  { order: 6; }
.app-col--order-7  { order: 7; }
.app-col--order-8  { order: 8; }
.app-col--order-9  { order: 9; }
.app-col--order-10 { order: 10; }
.app-col--order-11 { order: 11; }
.app-col--order-12 { order: 12; }

/* SM >= 576px */
@media (min-width: 576px) {
  .app-col--sm-auto   { flex: 1 1 0; max-width: 100%; }
  .app-col--sm-1  { flex: 0 0 calc((100% / 12) * 1);  max-width: calc((100% / 12) * 1); }
  .app-col--sm-2  { flex: 0 0 calc((100% / 12) * 2);  max-width: calc((100% / 12) * 2); }
  .app-col--sm-3  { flex: 0 0 calc((100% / 12) * 3);  max-width: calc((100% / 12) * 3); }
  .app-col--sm-4  { flex: 0 0 calc((100% / 12) * 4);  max-width: calc((100% / 12) * 4); }
  .app-col--sm-5  { flex: 0 0 calc((100% / 12) * 5);  max-width: calc((100% / 12) * 5); }
  .app-col--sm-6  { flex: 0 0 calc((100% / 12) * 6);  max-width: calc((100% / 12) * 6); }
  .app-col--sm-7  { flex: 0 0 calc((100% / 12) * 7);  max-width: calc((100% / 12) * 7); }
  .app-col--sm-8  { flex: 0 0 calc((100% / 12) * 8);  max-width: calc((100% / 12) * 8); }
  .app-col--sm-9  { flex: 0 0 calc((100% / 12) * 9);  max-width: calc((100% / 12) * 9); }
  .app-col--sm-10 { flex: 0 0 calc((100% / 12) * 10); max-width: calc((100% / 12) * 10); }
  .app-col--sm-11 { flex: 0 0 calc((100% / 12) * 11); max-width: calc((100% / 12) * 11); }
  .app-col--sm-12 { flex: 0 0 calc((100% / 12) * 12); max-width: calc((100% / 12) * 12); }
  .app-col--offset-sm-1  { margin-left: calc((100% / 12) * 1); }
  .app-col--offset-sm-2  { margin-left: calc((100% / 12) * 2); }
  .app-col--offset-sm-3  { margin-left: calc((100% / 12) * 3); }
  .app-col--offset-sm-4  { margin-left: calc((100% / 12) * 4); }
  .app-col--offset-sm-5  { margin-left: calc((100% / 12) * 5); }
  .app-col--offset-sm-6  { margin-left: calc((100% / 12) * 6); }
  .app-col--offset-sm-7  { margin-left: calc((100% / 12) * 7); }
  .app-col--offset-sm-8  { margin-left: calc((100% / 12) * 8); }
  .app-col--offset-sm-9  { margin-left: calc((100% / 12) * 9); }
  .app-col--offset-sm-10 { margin-left: calc((100% / 12) * 10); }
  .app-col--offset-sm-11 { margin-left: calc((100% / 12) * 11); }
  .app-col--order-sm-1  { order: 1; }
  .app-col--order-sm-2  { order: 2; }
  .app-col--order-sm-3  { order: 3; }
  .app-col--order-sm-4  { order: 4; }
  .app-col--order-sm-5  { order: 5; }
  .app-col--order-sm-6  { order: 6; }
  .app-col--order-sm-7  { order: 7; }
  .app-col--order-sm-8  { order: 8; }
  .app-col--order-sm-9  { order: 9; }
  .app-col--order-sm-10 { order: 10; }
  .app-col--order-sm-11 { order: 11; }
  .app-col--order-sm-12 { order: 12; }
}

/* MD >= 768px */
@media (min-width: 768px) {
  .app-col--md-auto   { flex: 1 1 0; max-width: 100%; }
  .app-col--md-1  { flex: 0 0 calc((100% / 12) * 1);  max-width: calc((100% / 12) * 1); }
  .app-col--md-2  { flex: 0 0 calc((100% / 12) * 2);  max-width: calc((100% / 12) * 2); }
  .app-col--md-3  { flex: 0 0 calc((100% / 12) * 3);  max-width: calc((100% / 12) * 3); }
  .app-col--md-4  { flex: 0 0 calc((100% / 12) * 4);  max-width: calc((100% / 12) * 4); }
  .app-col--md-5  { flex: 0 0 calc((100% / 12) * 5);  max-width: calc((100% / 12) * 5); }
  .app-col--md-6  { flex: 0 0 calc((100% / 12) * 6);  max-width: calc((100% / 12) * 6); }
  .app-col--md-7  { flex: 0 0 calc((100% / 12) * 7);  max-width: calc((100% / 12) * 7); }
  .app-col--md-8  { flex: 0 0 calc((100% / 12) * 8);  max-width: calc((100% / 12) * 8); }
  .app-col--md-9  { flex: 0 0 calc((100% / 12) * 9);  max-width: calc((100% / 12) * 9); }
  .app-col--md-10 { flex: 0 0 calc((100% / 12) * 10); max-width: calc((100% / 12) * 10); }
  .app-col--md-11 { flex: 0 0 calc((100% / 12) * 11); max-width: calc((100% / 12) * 11); }
  .app-col--md-12 { flex: 0 0 calc((100% / 12) * 12); max-width: calc((100% / 12) * 12); }
  .app-col--offset-md-1  { margin-left: calc((100% / 12) * 1); }
  .app-col--offset-md-2  { margin-left: calc((100% / 12) * 2); }
  .app-col--offset-md-3  { margin-left: calc((100% / 12) * 3); }
  .app-col--offset-md-4  { margin-left: calc((100% / 12) * 4); }
  .app-col--offset-md-5  { margin-left: calc((100% / 12) * 5); }
  .app-col--offset-md-6  { margin-left: calc((100% / 12) * 6); }
  .app-col--offset-md-7  { margin-left: calc((100% / 12) * 7); }
  .app-col--offset-md-8  { margin-left: calc((100% / 12) * 8); }
  .app-col--offset-md-9  { margin-left: calc((100% / 12) * 9); }
  .app-col--offset-md-10 { margin-left: calc((100% / 12) * 10); }
  .app-col--offset-md-11 { margin-left: calc((100% / 12) * 11); }
  .app-col--order-md-1  { order: 1; }
  .app-col--order-md-2  { order: 2; }
  .app-col--order-md-3  { order: 3; }
  .app-col--order-md-4  { order: 4; }
  .app-col--order-md-5  { order: 5; }
  .app-col--order-md-6  { order: 6; }
  .app-col--order-md-7  { order: 7; }
  .app-col--order-md-8  { order: 8; }
  .app-col--order-md-9  { order: 9; }
  .app-col--order-md-10 { order: 10; }
  .app-col--order-md-11 { order: 11; }
  .app-col--order-md-12 { order: 12; }
}

/* LG >= 992px */
@media (min-width: 992px) {
  .app-col--lg-auto   { flex: 1 1 0; max-width: 100%; }
  .app-col--lg-1  { flex: 0 0 calc((100% / 12) * 1);  max-width: calc((100% / 12) * 1); }
  .app-col--lg-2  { flex: 0 0 calc((100% / 12) * 2);  max-width: calc((100% / 12) * 2); }
  .app-col--lg-3  { flex: 0 0 calc((100% / 12) * 3);  max-width: calc((100% / 12) * 3); }
  .app-col--lg-4  { flex: 0 0 calc((100% / 12) * 4);  max-width: calc((100% / 12) * 4); }
  .app-col--lg-5  { flex: 0 0 calc((100% / 12) * 5);  max-width: calc((100% / 12) * 5); }
  .app-col--lg-6  { flex: 0 0 calc((100% / 12) * 6);  max-width: calc((100% / 12) * 6); }
  .app-col--lg-7  { flex: 0 0 calc((100% / 12) * 7);  max-width: calc((100% / 12) * 7); }
  .app-col--lg-8  { flex: 0 0 calc((100% / 12) * 8);  max-width: calc((100% / 12) * 8); }
  .app-col--lg-9  { flex: 0 0 calc((100% / 12) * 9);  max-width: calc((100% / 12) * 9); }
  .app-col--lg-10 { flex: 0 0 calc((100% / 12) * 10); max-width: calc((100% / 12) * 10); }
  .app-col--lg-11 { flex: 0 0 calc((100% / 12) * 11); max-width: calc((100% / 12) * 11); }
  .app-col--lg-12 { flex: 0 0 calc((100% / 12) * 12); max-width: calc((100% / 12) * 12); }
  .app-col--offset-lg-1  { margin-left: calc((100% / 12) * 1); }
  .app-col--offset-lg-2  { margin-left: calc((100% / 12) * 2); }
  .app-col--offset-lg-3  { margin-left: calc((100% / 12) * 3); }
  .app-col--offset-lg-4  { margin-left: calc((100% / 12) * 4); }
  .app-col--offset-lg-5  { margin-left: calc((100% / 12) * 5); }
  .app-col--offset-lg-6  { margin-left: calc((100% / 12) * 6); }
  .app-col--offset-lg-7  { margin-left: calc((100% / 12) * 7); }
  .app-col--offset-lg-8  { margin-left: calc((100% / 12) * 8); }
  .app-col--offset-lg-9  { margin-left: calc((100% / 12) * 9); }
  .app-col--offset-lg-10 { margin-left: calc((100% / 12) * 10); }
  .app-col--offset-lg-11 { margin-left: calc((100% / 12) * 11); }
  .app-col--order-lg-1  { order: 1; }
  .app-col--order-lg-2  { order: 2; }
  .app-col--order-lg-3  { order: 3; }
  .app-col--order-lg-4  { order: 4; }
  .app-col--order-lg-5  { order: 5; }
  .app-col--order-lg-6  { order: 6; }
  .app-col--order-lg-7  { order: 7; }
  .app-col--order-lg-8  { order: 8; }
  .app-col--order-lg-9  { order: 9; }
  .app-col--order-lg-10 { order: 10; }
  .app-col--order-lg-11 { order: 11; }
  .app-col--order-lg-12 { order: 12; }
}

/* XL >= 1200px */
@media (min-width: 1200px) {
  .app-col--xl-auto   { flex: 1 1 0; max-width: 100%; }
  .app-col--xl-1  { flex: 0 0 calc((100% / 12) * 1);  max-width: calc((100% / 12) * 1); }
  .app-col--xl-2  { flex: 0 0 calc((100% / 12) * 2);  max-width: calc((100% / 12) * 2); }
  .app-col--xl-3  { flex: 0 0 calc((100% / 12) * 3);  max-width: calc((100% / 12) * 3); }
  .app-col--xl-4  { flex: 0 0 calc((100% / 12) * 4);  max-width: calc((100% / 12) * 4); }
  .app-col--xl-5  { flex: 0 0 calc((100% / 12) * 5);  max-width: calc((100% / 12) * 5); }
  .app-col--xl-6  { flex: 0 0 calc((100% / 12) * 6);  max-width: calc((100% / 12) * 6); }
  .app-col--xl-7  { flex: 0 0 calc((100% / 12) * 7);  max-width: calc((100% / 12) * 7); }
  .app-col--xl-8  { flex: 0 0 calc((100% / 12) * 8);  max-width: calc((100% / 12) * 8); }
  .app-col--xl-9  { flex: 0 0 calc((100% / 12) * 9);  max-width: calc((100% / 12) * 9); }
  .app-col--xl-10 { flex: 0 0 calc((100% / 12) * 10); max-width: calc((100% / 12) * 10); }
  .app-col--xl-11 { flex: 0 0 calc((100% / 12) * 11); max-width: calc((100% / 12) * 11); }
  .app-col--xl-12 { flex: 0 0 calc((100% / 12) * 12); max-width: calc((100% / 12) * 12); }
  .app-col--offset-xl-1  { margin-left: calc((100% / 12) * 1); }
  .app-col--offset-xl-2  { margin-left: calc((100% / 12) * 2); }
  .app-col--offset-xl-3  { margin-left: calc((100% / 12) * 3); }
  .app-col--offset-xl-4  { margin-left: calc((100% / 12) * 4); }
  .app-col--offset-xl-5  { margin-left: calc((100% / 12) * 5); }
  .app-col--offset-xl-6  { margin-left: calc((100% / 12) * 6); }
  .app-col--offset-xl-7  { margin-left: calc((100% / 12) * 7); }
  .app-col--offset-xl-8  { margin-left: calc((100% / 12) * 8); }
  .app-col--offset-xl-9  { margin-left: calc((100% / 12) * 9); }
  .app-col--offset-xl-10 { margin-left: calc((100% / 12) * 10); }
  .app-col--offset-xl-11 { margin-left: calc((100% / 12) * 11); }
  .app-col--order-xl-1  { order: 1; }
  .app-col--order-xl-2  { order: 2; }
  .app-col--order-xl-3  { order: 3; }
  .app-col--order-xl-4  { order: 4; }
  .app-col--order-xl-5  { order: 5; }
  .app-col--order-xl-6  { order: 6; }
  .app-col--order-xl-7  { order: 7; }
  .app-col--order-xl-8  { order: 8; }
  .app-col--order-xl-9  { order: 9; }
  .app-col--order-xl-10 { order: 10; }
  .app-col--order-xl-11 { order: 11; }
  .app-col--order-xl-12 { order: 12; }
}

/* Align self */
.app-col--align-self-start   { align-self: flex-start; }
.app-col--align-self-center  { align-self: center; }
.app-col--align-self-end     { align-self: flex-end; }
.app-col--align-self-stretch { align-self: stretch; }
</style>